import { version as appVersion } from 'package.json';
import { ID } from 'shared/types/id';
import { isApnsSupported } from 'shared/utils/is-apns-supported';

const storageKey = 'push-notification-tokens';

type TokenEntry = {
  token: string;
  appVersion: string;
  electronVersion?: string;
};

type StorageValue = string | TokenEntry;

const getPushNotificationTokens = (): Record<ID, StorageValue> => {
  const tokensStr = window.localStorage.getItem(storageKey);
  try {
    const tokens = tokensStr ? JSON.parse(tokensStr) ?? {} : {};
    return tokens;
  } catch (e) {
    return {};
  }
};

const isTokenValid = (entry: StorageValue): string | undefined => {
  // Old format: plain string
  if (typeof entry === 'string') {
    // On APNs-supported Electron, old tokens are stale and need re-creation
    if (isApnsSupported()) {
      return undefined;
    }
    return entry;
  }

  // New format: invalidate tokens from Electron < 2.4.0 on APNs-supported Mac
  // Missing electronVersion means it was stored before version tracking — also stale
  if (isApnsSupported()) {
    if (!entry.electronVersion) {
      return undefined;
    }
    const [major, minor] = entry.electronVersion.split('.').map(Number);
    if (major < 2 || (major === 2 && minor < 4)) {
      return undefined;
    }
  }

  return entry.token;
};

export const getPushNotificationToken = (uid: ID): string | undefined => {
  const entry = getPushNotificationTokens()[uid];
  if (!entry) {
    return undefined;
  }
  return isTokenValid(entry);
};

/** Returns the raw token string from storage regardless of validity, for stale token cleanup. */
export const getRawStoredToken = (uid: ID): string | undefined => {
  const entry = getPushNotificationTokens()[uid];
  if (!entry) {
    return undefined;
  }
  return typeof entry === 'string' ? entry : entry.token;
};

export const getUidByPushNotificationToken = (token: string) => {
  const tokens = getPushNotificationTokens();
  return Object.keys(tokens).find((key) => {
    const entry = tokens[key];
    const entryToken = typeof entry === 'string' ? entry : entry.token;
    return entryToken === token;
  });
};

export const setPushNotificationToken = (uid: string, token: string) => {
  const entry: TokenEntry = {
    token,
    appVersion,
    electronVersion: window.electron?.version,
  };
  window.localStorage.setItem(storageKey, JSON.stringify({ [uid]: entry }));
};

export const clearPushNotificationTokens = () =>
  window.localStorage.removeItem(storageKey);

export const removePushNotificationToken = (uid: ID) => {
  const tokens = getPushNotificationTokens();
  delete tokens[uid];
  window.localStorage.setItem(storageKey, JSON.stringify(tokens));
};
